/**
 * A part's text fields — its script, and its notes — edited together (PLAN §6.2 "Two people edit the same
 * script": Yjs merges character by character). Every input emits the text
 * the user typed into and the text it became (`onChange(prev, next)`); the
 * view replays that one edit onto the newest text (`rebaseEdit` — others'
 * typing may have landed in between) and turns it into a Yjs update of just
 * the changed span (`lib/editor/patches.ts`'s `textPatch`). The merged text
 * comes back as `text`.
 *
 * The textarea is deliberately **uncontrolled**: when `text` changes because
 * of someone else's typing, the new text is written into it directly and the
 * caret is moved by `transformSelection`, so it stays where the user was
 * typing instead of jumping to the end. Ctrl/⌘+Z are the editor's undo, not
 * the browser's (whose history the remote writes would corrupt).
 *
 * `field` picks which of the part's Yjs text fields (`TEXT_FIELDS.part`) it
 * edits: the presenter's `script`, or the production `notes` (for the crew,
 * never read on air). Both merge the same way.
 */
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { transformSelection } from '../../lib/editor/caret.js';
import type { RowPresence } from '../../lib/editor/presence.js';
import { cn } from '../../lib/utils.js';
import { Label } from '../ui/label.js';
import { presenceClass } from './PresenceAvatars.js';

export type TextField = 'script' | 'notes';

const FIELD_TEXT: Readonly<Record<TextField, { readonly label: string; readonly placeholder: string; readonly rows: number }>> = {
  script: { label: 'Script', placeholder: 'What the presenter reads…', rows: 10 },
  notes: { label: 'Notes', placeholder: 'Notes for the crew (not read on air)…', rows: 4 },
};

const HEIGHT: Readonly<Record<TextField, string>> = { script: 'min-h-40', notes: 'min-h-20' };

export type ScriptPanelProps = {
  readonly field: TextField;
  readonly text: string;
  /** Others editing this script right now. */
  readonly editors: readonly RowPresence[];
  /** `prev`: the text as the textarea showed it before this input; `next`: after. */
  readonly onChange: (prev: string, next: string) => void;
  readonly onFocusChange: (focused: boolean) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
};

export function ScriptPanel({ field, text, editors, onChange, onFocusChange, onUndo, onRedo }: ScriptPanelProps) {
  const copy = FIELD_TEXT[field];
  const ref = useRef<HTMLTextAreaElement | null>(null);
  /** What the textarea showed after the last input or sync: the base of the next input. */
  const shown = useRef(text);
  // The mount-time text only: a CHANGING `defaultValue` makes React rewrite an
  // untouched textarea's value itself, behind `shown`'s back (the two-user e2e
  // test caught the duplicated text that caused).
  const [initial] = useState(text);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    shown.current = text;
    if (el.value === text) return;
    const focused = el.ownerDocument.activeElement === el;
    const selection = transformSelection(el.value, text, { start: el.selectionStart, end: el.selectionEnd });
    el.value = text;
    if (focused) el.setSelectionRange(selection.start, selection.end);
  }, [text]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key === 'z') {
      e.preventDefault();
      if (e.shiftKey) onRedo();
      else onUndo();
    } else if (key === 'y') {
      e.preventDefault();
      onRedo();
    }
  };

  return (
    <section className="grid gap-1 border-t pt-3" data-testid={`${field}-panel`} aria-label={copy.label}>
      <div className="flex items-center gap-2">
        <Label htmlFor={`part-${field}`} className="text-xs text-muted-foreground">
          {copy.label}
        </Label>
        {editors.length === 0 ? null : (
          <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground" data-testid={`${field}-editors`}>
            {editors.map((p) => (
              <span key={p.userId} className={cn('rounded-sm px-1', presenceClass(p.colour))}>
                {p.initials}
              </span>
            ))}
            typing
          </span>
        )}
      </div>
      <textarea
        ref={ref}
        id={`part-${field}`}
        data-testid={`${field}-text`}
        defaultValue={initial}
        rows={copy.rows}
        spellCheck
        placeholder={copy.placeholder}
        className={cn(HEIGHT[field], 'w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm leading-relaxed shadow-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50')}
        onInput={(e) => {
          const prev = shown.current;
          shown.current = e.currentTarget.value;
          onChange(prev, e.currentTarget.value);
        }}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        onKeyDown={onKeyDown}
      />
    </section>
  );
}
